import { UserRole } from '@garden-platform/shared';
import { AlertCircle, Loader2, Shield, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { getSessionStats, resendVerification } from '../../services/auth';

interface SessionStats {
  activeSessions: number;
  totalSessions: number;
  activeUsers: number;
  totalMembers: number;
}

export default function Overview() {
  const { user, organization } = useAuth();
  const [stats, setStats] = useState<SessionStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const isAdmin = user?.role === UserRole.ADMIN;

  const loadStats = useCallback(async () => {
    setIsLoadingStats(true);
    try {
      const result = await getSessionStats();
      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
    setIsLoadingStats(false);
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadStats();
    }
  }, [isAdmin, loadStats]);

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const result = await resendVerification(user?.email);
      if (result.success) {
        setVerificationSent(true);
      }
    } catch (error) {
      console.error('Failed to resend verification:', error);
    }
    setResendingVerification(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Welcome back, {user?.name || user?.email}!</p>
      </div>

      {/* Email verification warning */}
      {user && !user.emailVerified && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Email not verified</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>Please verify your email address to access all features.</span>
            {!verificationSent ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResendVerification}
                disabled={resendingVerification}
              >
                {resendingVerification ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Resend verification'
                )}
              </Button>
            ) : (
              <span className="text-sm">Verification email sent!</span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Stats cards for admins */}
      {isAdmin && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Members</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoadingStats ? '-' : stats?.totalMembers || 0}
              </div>
              <p className="text-xs text-muted-foreground">In {organization?.name}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoadingStats ? '-' : stats?.activeUsers || 0}
              </div>
              <p className="text-xs text-muted-foreground">With active sessions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Sessions</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoadingStats ? '-' : stats?.activeSessions || 0}
              </div>
              <p className="text-xs text-muted-foreground">Across all users</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoadingStats ? '-' : stats?.totalSessions || 0}
              </div>
              <p className="text-xs text-muted-foreground">All time</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* User info card */}
      <Card>
        <CardHeader>
          <CardTitle>Your Account</CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Email</span>
            <span>{user?.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Name</span>
            <span>{user?.name || 'Not set'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Role</span>
            <span className="capitalize">{user?.role}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Organization</span>
            <span>{organization?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Email Verified</span>
            <span>{user?.emailVerified ? 'Yes' : 'No'}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
